import { store } from '@/app/store';
import { activeRightStack } from '@/app/store/navigationSlice';
import { deleteChatHistory } from '@/features/chats/model/conversation';
import { deleteRecentChat } from '@/features/chats/model/recentChat';
import { upsertRecentChatItem } from '@/features/chats/model/recentChat/reducer';
import { deleteChatAction } from '@/features/chats/sub-features/chat-info/utils/actionUtils';
import {
   appendRequestUsers,
   removeAllRequests,
   setInviteUserCount,
   setRequestUsers,
   setRequestUsersClearing,
   setRequestUsersHasMore,
   setRequestUsersLoading,
   updateInviteStatus,
} from '@/features/invite/model/inviteSlice';
import { acceptInvitation, deleteAllRequests, getInviteRequestInfo, getRequestUserList } from '@/infrastructure/sdk';
import { createLogger } from '@/infrastructure/sync/utils';
import { i18n } from '@/localization/i18n';
import { getUserIdFromJid } from '@/shared/utils';
import { showErrorToast } from '@/shared/utils/toastMessage';
import { syncUserProfiles } from './profiles';

const { log } = createLogger('InviteSync');

const getUsersFromResponse = response => response?.data ?? [];
const REQUEST_USERS_FETCH_LIMIT = 20;

const fetchRequestUsers = async (messageTime = 0) => {
   const dispatch = store.dispatch;
   dispatch(setRequestUsersLoading(true));

   try {
      const response = await getRequestUserList({ limit: REQUEST_USERS_FETCH_LIMIT, messageTime });

      if (!response || response.statusCode !== 200) {
         throw new Error(response?.message ?? 'toast.SOMETHING_WENT_WRONG_RETRY');
      }

      const users = getUsersFromResponse(response);
      log('Get', `Fetched ${users.length} users.`);

      // The message-requests count now comes from this endpoint's own inviteUserCount
      // (previously derived from the recent-chats pagination response instead).
      if (response.inviteUserCount !== undefined && response.inviteUserCount !== null) {
         dispatch(setInviteUserCount(Number(response.inviteUserCount)));
      }

      return users;
   } catch (err) {
      log('Error', `fetchRequestUsers failed: ${err.message}`);
      return null;
   } finally {
      dispatch(setRequestUsersLoading(false));
   }
};

export const InviteSync = {
   async enrichUserData(users) {
      if (!users?.length) return;
      const userIds = [...new Set(users.map(u => u.fromUserId).filter(Boolean))];
      if (userIds.length > 0) {
         await syncUserProfiles(userIds, { isSyncCheckNeeded: false });
      }
   },

   async getRequestUsers() {
      const users = (await fetchRequestUsers(0)) || [];

      await this.enrichUserData(users);
      store.dispatch(setRequestUsers({ users }));
      store.dispatch(setRequestUsersHasMore(users.length > 0 && users.length >= REQUEST_USERS_FETCH_LIMIT));
      return users;
   },

   async loadMoreRequestUsers(messageTime) {
      const users = (await fetchRequestUsers(messageTime)) || [];

      if (users.length > 0) {
         await this.enrichUserData(users);
         store.dispatch(appendRequestUsers({ users }));
      }
      store.dispatch(setRequestUsersHasMore(users.length > 0 && users.length >= REQUEST_USERS_FETCH_LIMIT));
      return users;
   },

   async clearAllRequests() {
      try {
         const dispatch = store.dispatch;
         dispatch(setRequestUsersClearing(true));
         try {
            const requestUsers = store.getState().inviteData?.requestUsers || [];
            const response = await deleteAllRequests(Date.now());
            if (!response || response?.statusCode !== 200) {
               showErrorToast(i18n.t(response?.message || 'toast.ACTION_FAILED'));
               return false;
            }

            requestUsers.forEach(user => {
               const fromUserId = user.fromUserId;
               if (fromUserId) {
                  dispatch(deleteChatHistory({ fromUserId }));
                  dispatch(deleteRecentChat({ fromUserId }));
               }
            });

            dispatch(removeAllRequests());
            dispatch(setRequestUsersHasMore(false));
            dispatch(activeRightStack({ activeStack: '', activeChatId: '', activeChatJid: '', activeCallLog: null }));
            return true;
         } catch (err) {
            log('Error', `clearAllRequests failed: ${err.message}`);
            return false;
         } finally {
            dispatch(setRequestUsersClearing(false));
         }
      } catch (err) {
         log('Error', `clearAllRequests failed: ${err.message}`);
         showErrorToast(i18n.t(err.message || 'toast.ACTION_FAILED'));
      }
   },

   async acceptRequest(jid) {
      try {
         const response = await acceptInvitation(jid);
         if (!response || response.statusCode !== 200) {
            throw new Error(response?.message ?? 'toast.ACTION_FAILED');
         }

         const userId = getUserIdFromJid(jid);
         const state = store.getState();
         const requestEntry = state.inviteData?.requestUsers?.find(u => getUserIdFromJid(u.fromUserId) === userId);
         const sdkInfo = (await getInviteRequestInfo(jid))?.data || {};
         store.dispatch(
            updateInviteStatus({
               userId,
               inviteStatus: sdkInfo.inviteStatus ?? 2,
               isSender: sdkInfo.isSender ?? false,
               remainingMessages: sdkInfo.remainingMessages,
            }),
         );
         if (requestEntry) {
            store.dispatch(upsertRecentChatItem(requestEntry));
         }

         return true;
      } catch (err) {
         log('Error', `acceptRequest failed: ${err.message}`);
         showErrorToast(i18n.t(err.message || 'toast.ACTION_FAILED'));
         return false;
      }
   },

   async rejectRequest(jid) {
      try {
         const response = await deleteChatAction(jid);
         if (!response || response.statusCode !== 200) {
            throw new Error(response?.message ?? 'toast.ACTION_FAILED');
         }
         log('Reject', `Request deleted (via deleteChat) for ${jid}`);
         return true;
      } catch (err) {
         log('Error', `rejectRequest failed: ${err.message}`);
         showErrorToast(i18n.t(err.message || 'toast.ACTION_FAILED'));
         return false;
      }
   },
};
